import { EventIcon } from "@faire/deprecated-retailer-visitor-shared/components/Events/shared/EventIcon";
import { useEventsStore } from "@faire/marketplace-lib/stores/events/shared/EventsStore";
import { Core } from "@faire/slate/foundation/color";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { themeOrDefault } from "@faire/web/slate/Theme";
import { ThemeProvider } from "@faire/web/slate/Theme/ThemeProvider";
import { Typography } from "@faire/web/slate/Typography";
import React, { type JSX } from "react";
import styled, { css } from "styled-components";

export enum PromoLineVariant {
  DEFAULT = "default",
  SMALL = "small",
}

interface IProps {
  text: string | JSX.Element | null;
  variant?: PromoLineVariant;
  centered?: boolean;
  fullWidth?: boolean;
  border?: boolean;
  mediumText?: boolean;
}

export const EventPromoLine: React.FC<IProps> = ({
  text,
  variant,
  centered,
  fullWidth,
  border,
  mediumText,
}) => {
  const { isMarketEvent, nextOrActiveEventTheme } = useEventsStore();

  const properPadding =
    variant === PromoLineVariant.SMALL
      ? getSpacing(10, "2x", 10, "1x")
      : getSpacing(12, "2x", 12, "2x");

  return (
    <ThemeProvider theme={nextOrActiveEventTheme}>
      <Container
        $padding={properPadding}
        $alignItems="center"
        $centered={centered}
        $background={nextOrActiveEventTheme.eventsThemeColors?.lightBackground}
        $fullWidth={fullWidth}
        $border={border}
      >
        <Column>
          <EventIcon
            size={isMarketEvent ? getSpacing("3x") : getSpacing("2x")}
          />
        </Column>
        <Spacer width={variant === PromoLineVariant.SMALL ? "1x" : "2x"} />
        <Column>
          <SingleLineTypography
            variant={
              mediumText ? "paragraphSansMedium" : "paragraphSansRegular"
            }
          >
            {text}
          </SingleLineTypography>
        </Column>
      </Container>
    </ThemeProvider>
  );
};

const SingleLineTypography = styled(Typography)`
  &&& {
    line-height: unset;
    color: ${(props) =>
      themeOrDefault(props.theme).eventsThemeColors?.promoText};
  }
`;

// eslint-disable-next-line @faire/prefer-slate-attrs -- move align and justify to a prop
const Container = styled(Row)<{
  $padding: string;
  $alignItems?: string;
  $centered?: boolean;
  $background?: string;
  $fullWidth?: boolean;
  $border?: boolean;
}>`
  ${(props) =>
    props.$border
      ? css`
          border: 1px solid ${Core.border.muted};
        `
      : null}
  padding: ${(props) => props.$padding};
  align-items: ${(props) => props.$alignItems ?? "flex-start"};
  background: ${(props) =>
    themeOrDefault(props.theme).eventsThemeColors?.lightBackground};
  justify-content: ${(props) => (props.$centered ? "center" : "left")};

  ${(props) =>
    props.$fullWidth
      ? css`
          width: 100%;
        `
      : null}
`;
